/**
 * Supabase auth links (invite, password recovery, sign-up confirmation, magic
 * link) land on the app with the session in the URL fragment, e.g.
 * `https://app/#access_token=..&refresh_token=..&type=invite`, or with an
 * error (`#error=access_denied&error_code=otp_expired&error_description=..`).
 *
 * Left alone, that fragment breaks the app: ra-supabase-core's checkAuth sees
 * `type=invite` and throws, react-admin's `requireAuth` treats the throw as
 * "not authenticated", mounts `<LogoutOnMount>` and `signOut()` revokes the
 * brand new session. The invited user ends up on a mute login page.
 *
 * This module turns such a URL into the in-app hash route that handles it
 * (`#/set-password?..` for invite/recovery). It must run before the router,
 * supabase-js (`detectSessionInUrl`) and checkAuth see the URL: it is imported
 * first in `main.tsx`, and called again from checkAuth / on hashchange.
 *
 * It does not depend on the Supabase redirect URL or email template: it works
 * whether the link lands on `/`, `/auth-callback.html` or any other path.
 */

/** Types whose user must choose a password on `#/set-password`. */
const SET_PASSWORD_TYPES: ReadonlySet<string> = new Set(["invite", "recovery"]);
/** Types that only need the session stored, via `#/auth-callback`. */
const SESSION_TYPES: ReadonlySet<string> = new Set([
  "signup",
  "magiclink",
  "email_change",
]);
const ERROR_KEYS = ["error", "error_code", "error_description"] as const;

/**
 * `#/forgot-password` route for an auth link error, prefilled with `email`
 * when it looks like an email address.
 */
export const getForgotPasswordPath = (email: string | null): string =>
  email && /^[^\s@]+@[^\s@]+$/.test(email)
    ? `/forgot-password?${new URLSearchParams({ email })}`
    : "/forgot-password";

/** Query param of `#/login` carrying the Supabase error code of a bad link. */
export const AUTH_ERROR_PARAM = "auth_error";

export interface AuthUrlLocation {
  pathname: string;
  search: string;
  hash: string;
}

/** Parses a non-route fragment (`#access_token=..`), or null for `#/route`. */
const readFragmentParams = (hash: string): URLSearchParams | null => {
  const fragment = hash.replace(/^#/, "");
  // `#/set-password?..`, `#/oauth/consent?..`, `#/p/..`: already routed.
  if (fragment === "" || fragment.startsWith("/")) {
    return null;
  }
  return new URLSearchParams(fragment);
};

/**
 * Returns the hash route an auth callback URL must be rewritten to, or null
 * when the URL is not a Supabase auth callback (normal navigation, SSO/OAuth
 * fragments without `type`, PKCE `?code=` handled by supabase-js).
 */
export const getAuthCallbackHash = (
  location: AuthUrlLocation,
): string | null => {
  const params = readFragmentParams(location.hash);
  if (params == null) {
    return null;
  }

  if (ERROR_KEYS.some((key) => params.has(key))) {
    const code =
      params.get("error_code") || params.get("error") || "unknown_error";
    const query = new URLSearchParams({ [AUTH_ERROR_PARAM]: code });
    // Supabase does not send it today; kept if present to prefill the
    // "request a new link" form.
    const email = params.get("email");
    if (email) {
      query.set("email", email);
    }
    return `#/login?${query}`;
  }

  const accessToken = params.get("access_token");
  const refreshToken = params.get("refresh_token");
  const type = params.get("type");
  if (!accessToken || !refreshToken || !type) {
    return null;
  }

  const query = new URLSearchParams({
    access_token: accessToken,
    refresh_token: refreshToken,
    type,
  });
  if (SET_PASSWORD_TYPES.has(type)) {
    return `#/set-password?${query}`;
  }
  if (SESSION_TYPES.has(type)) {
    return `#/auth-callback?${query}`;
  }
  return null;
};

/**
 * Full URL (path + search + hash) the current URL must be replaced with, or
 * null. A landing on `/auth-callback(.html)` (served by the PWA service worker
 * as the app shell) is moved back to the app root next to it.
 */
export const getAuthCallbackRedirect = (
  location: AuthUrlLocation,
): string | null => {
  const hash = getAuthCallbackHash(location);
  if (hash == null) {
    return null;
  }
  const pathname = location.pathname.replace(/auth-callback(\.html)?$/, "");
  return `${pathname}${location.search}${hash}`;
};

/**
 * Rewrites the current URL if it is a Supabase auth callback. Uses
 * `location.replace` so the tokens do not stay in the history and, the path
 * being unchanged in the common case, it is a same-document navigation that
 * the hash router picks up. Returns whether a rewrite happened.
 */
export const rewriteAuthCallbackUrl = (
  location: AuthUrlLocation & { replace: (url: string) => void } = window
    .location,
): boolean => {
  const target = getAuthCallbackRedirect(location);
  if (target == null) {
    return false;
  }
  location.replace(target);
  return true;
};

/**
 * `redirectTo` for Supabase emails sent from the browser (password reset):
 * `auth-callback.html` next to the current app shell, so it also works when
 * the app is served under a sub-path. Must be allowed in Supabase Auth >
 * URL Configuration > Redirect URLs, otherwise Supabase falls back to the
 * Site URL (still handled by the boot rewrite).
 */
export const getAuthCallbackUrl = (
  location: Pick<Location, "origin" | "pathname"> = window.location,
): string =>
  new URL("auth-callback.html", `${location.origin}${location.pathname}`).href;

/**
 * `detectSessionInUrl` for supabase-js: same as its default check, except it
 * leaves invite / recovery links to `#/set-password`, which stores the session
 * itself once the password is chosen. Guards against the boot rewrite not
 * having run (e.g. a cached old shell).
 */
export const shouldDetectSessionInUrl = (
  _url: URL,
  params: Record<string, string>,
): boolean =>
  !SET_PASSWORD_TYPES.has(params.type ?? "") &&
  Boolean(
    params.access_token ||
    params.error ||
    params.error_description ||
    params.error_code,
  );

/**
 * True when the URL carries Supabase session tokens of an auth link, either
 * raw or already rewritten to `#/set-password` / `#/auth-callback`. Logging
 * out there would revoke the session the link just granted.
 */
export const hasAuthLinkTokens = (location: AuthUrlLocation): boolean => {
  const hash = getAuthCallbackHash(location) ?? location.hash;
  const match = /^#\/(set-password|auth-callback)\?(.*)$/.exec(hash);
  return match != null && new URLSearchParams(match[2]).has("access_token");
};
